import { useCallback, useEffect } from 'react'

import {
  type OpenableControls,
  useOpenableControls,
} from '../../../generic-hooks'
import { useGlobalConfigContext } from '../../../global-config'
import {
  gtmDataLayerPushRxPdpAppDrawerClose,
  gtmDataLayerPushRxPdpAppDrawerView,
} from '../../../gtm'
import { TENANT } from '../../../tenant'
import { useRxPdpAppRedemptionTracking } from '../ProductAppRedemption/useRxPdpAppRedemptionTracking'

export type ProductPrescriptionDrawerControls = Pick<
  OpenableControls,
  'handleClose' | 'handleOpen' | 'isOpen'
> & {
  handleEnter: () => void
}

/** Combines drawer state with view and close tracking while preserving legacy tenants. */
export const useProductPrescriptionDrawerControls =
  (): ProductPrescriptionDrawerControls => {
    const { handleClose: close, handleOpen, isOpen } = useOpenableControls()
    const { tenant } = useGlobalConfigContext()
    const tracking = useRxPdpAppRedemptionTracking()

    useEffect(() => {
      if (isOpen) {
        tracking?.track({ action: 'drawer_open', placement: 'drawer' })
      }
    }, [isOpen, tracking])

    const handleClose = useCallback((): void => {
      if (isOpen && tenant === TENANT.COM) {
        gtmDataLayerPushRxPdpAppDrawerClose()
      }
      close()
    }, [close, isOpen, tenant])

    const handleEnter = useCallback((): void => {
      if (tenant === TENANT.COM) {
        gtmDataLayerPushRxPdpAppDrawerView()
      }
    }, [tenant])

    return {
      handleClose,
      handleEnter,
      handleOpen,
      isOpen,
    }
  }
